import { Button, Box, Checkbox, Divider, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, FormControl, InputLabel, Select, FormHelperText, MenuItem, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

const STORAGE_KEY = "fps_terms_accepted_v3";

const TERMS = [
  {
    section: "FEE REGULATIONS / शुल्क नियमावली",
    points: [
      "Fee Deposit Schedule : April, May & June – deposit before 7th April",
      "Fee Deposit Schedule : July & August – deposit before 7th July",
      "Fee Deposit Schedule : September & October – deposit before 7th September",
      "Fee Deposit Schedule : November & December – deposit before 7th November",
      "Fee Deposit Schedule : January, February & March – deposit before 7th January",
      "Parents are informed salary disbursement is required by 7th of every month; please deposit fees on time.",
      "Examination Rule: Student will not be allowed in Final exams unless all dues are cleared.",
      "Late Fee Rule: Last date for depositing fee is 10th of the depositing month.",
      "Late Fee Rule: After due date, ₹25 late fee will be charged (or as revised by school).",
      "Withdrawal Rule: If written notice of withdrawal not given before 7th of the month, fee for following month(s) will be charged.",
      "Non-Refundable: Once deposited, school fee is non-refundable.",
      "Revision of Fee: Fee may be revised from time to time by the school.",
    ],
  },
  {
    section: "ADMISSION GUIDELINES / प्रवेश निर्देश",
    points: [
      "Parents must ensure that all information entered in the admission form is true and correct.",
      "Once a form is submitted for a candidate, another form for the same candidate will not be accepted.",
      "Age Requirement: For Pre-Nursery, the child should be 3 years old as on 1st March of the respective academic year.",
      "Interview Declaration: Interview with both parents and the child is part of admission process.",
      "Interview date & time will be declared one day after submission of online admission form through school website.",
      "Only candidates whose forms are submitted with correct information will be considered for admission based on interview of parents.",
      "Parents must bring the candidate to the interview and the candidate must be their own child.",
      "Parents must appear on prescribed date/time. No rescheduling after missing the interview.",
      "Admission is granted based on interview and availability of seats.",
      "School/Principal reserves the right to accept or decline admission.",
      "Status Check: Parents can use Admission Form Number to check application status and view interview date/time.",
    ],
  },
  {
    section: "RULES & DISCIPLINE / नियम एवं अनुशासन",
    points: [
      "Students must maintain cleanliness, politeness, honesty, discipline and respect for teachers and peers.",
      "Silence and discipline must be maintained during teaching hours.",
      "Any activity harming school discipline (writing on walls/benches, damaging property, objectionable behavior) is prohibited.",
      "Irregular attendance, laziness, disobedience, misbehavior or disrespect may lead to strict disciplinary action including dismissal.",
      "Any damage caused by the student must be compensated/rectified by parents.",
      "Students must not litter; use dustbins only.",
      "Unapproved items (books/comics/games) are not allowed without permission. Running/shouting is prohibited.",
      "Students are accountable for their behavior inside and outside the school.",
      "Late students cannot enter class without principal’s signature/permission marked in the diary.",
      "Parents must ensure child is dropped at least 5 minutes before assembly bell and picked up on time.",
      "Students cannot leave school premises during school hours without principal’s permission.",
      "Students must converse in English on school premises and maintain high standards in class.",
    ],
  },
  {
    section: "EXAMINATIONS & PROMOTIONS / परीक्षा एवं पदोन्नति",
    points: [
      "School conducts various tests in addition to terminal and final exams.",
      "Parents are informed about conduct, progress and attendance.",
      "Students absent from exam/test will not be re-examined and may lose marks.",
      "Absence without serious reason/prior written application may be treated as failure.",
      "Missing one or more subjects disqualifies student from rank/merit and prizes.",
      "Up to K.G., promotion granted on passing all subjects with minimum 40%. 60% is credit and 80%+ is distinction.",
      "School is not responsible for retaining answer scripts after report cards are issued.",
      "Minimum 75% attendance is mandatory for promotion.",
      "All promotion-related matters are at discretion of principal; reconsideration requests should be avoided.",
    ],
  },
  {
    section: "SCHOOL UNIFORM / स्कूल यूनिफॉर्म",
    points: [
      "Summer Uniform (Boys): Green half sleeve t-shirt with navy blue collar/shoulder/sleeve with school logo; navy blue shorts/full trousers with elastic waistband + drawstrings and white stripe.",
      "Summer Uniform (Girls): Red half sleeve t-shirt with navy blue collar/shoulder/sleeve with school logo; navy blue shorts/full trousers with elastic waistband + drawstrings and white stripe.",
      "Winter Uniform (Boys): Green full sleeve t-shirt with navy blue collar/shoulder/sleeve with school logo; school winter track suit.",
      "Winter Uniform (Girls): Red full sleeve t-shirt with navy blue collar/shoulder/sleeve with school logo; school winter track suit.",
    ],
  },
  {
    section: "VISITING HOURS / विजिटिंग आवर्स",
    points: [
      "Parents can meet principal on working days from 9:30 a.m. to 10:30 a.m. (April to October) and 10:00 a.m. to 11:30 a.m. (November to March) with prior appointment.",
    ],
  },
  {
    section: "RECOMMENDATIONS TO PARENTS/GUARDIANS / अभिभावकों हेतु सुझाव",
    points: [
      "Parents should cooperate by enforcing rules, supervising lessons, ensuring punctuality and proper uniform.",
      "Parents must read remarks in diary and acknowledge by signing when required.",
      "Parents should sign notebooks regularly and verify leave/absence records.",
      "Letters to principal must mention student’s name, class and section.",
      "Parents/guardians should not meet wards or teachers during school hours without permission.",
      "Avoid tipping non-teaching staff during/after school hours.",
    ],
  },
];

function TermsModal({ open, onAgree }) {
  const [sectionChecked, setSectionChecked] = useState(() =>
    Array(TERMS.length).fill(false)
  );
  const [finalConsent, setFinalConsent] = useState(false);

  useEffect(() => {
    if (open) {
      setSectionChecked(Array(TERMS.length).fill(false));
      setFinalConsent(false);
    }
  }, [open]);

  const allSectionsChecked = sectionChecked.every(Boolean);

  const handleSectionToggle = (index) => {
    setSectionChecked((prev) =>
      prev.map((v, i) => (i === index ? !v : v))
    );
  };

  const handleAccept = () => {
    onAgree?.();
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" disableEscapeKeyDown>
      <DialogTitle>
        Fatehpur Public School – Terms & Conditions (Parent Consent)
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Please read carefully. Tick each section after reading it completely.
          Admission form will open only after accepting all sections.
        </Typography>

        {TERMS.map((sec, sIndex) => (
          <Box key={sIndex} sx={{ mb: 3 }}>
            {/* ✅ SECTION CHECKBOX */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={sectionChecked[sIndex]}
                  onChange={() => handleSectionToggle(sIndex)}
                />
              }
              label={
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {sec.section}
                </Typography>
              }
            />

            <Divider sx={{ mb: 1 }} />

            {/* ✅ Points shown as text only */}
            <Box sx={{ pl: 4 }}>
              {sec.points.map((point, pIndex) => (
                <Typography key={pIndex} variant="body2" sx={{ mb: 0.8 }}>
                  • {point}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={finalConsent}
              onChange={(e) => setFinalConsent(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I/We as parent/guardian have read and understood all the above rules and agree to follow them.
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          disabled={!(allSectionsChecked && finalConsent)}
          onClick={handleAccept}
        >
          Agree & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const normalizeDate = (dateStr) => {
  const cleaned = dateStr.replace(/\s+/g, " ").replace("AM", " AM").replace("PM", " PM");
  return new Date(cleaned);
};

const getAdmissionWindowStatus = (publishDate) => {
  if (!publishDate) return "HARD_LOCK";

  const now = Date.now();
  const publish = normalizeDate(publishDate);

  if (isNaN(publish.getTime())) {
    console.error("Invalid publishDate:", publishDate);
    return "HARD_LOCK";
  }

  return now >= publish.getTime() ? "OPEN" : "HARD_LOCK";
};

function InfoNoticeModal({ open, lockType, isMaxed, onClose, academicYear, publishDate }) {
  const isHardLock = lockType === "HARD_LOCK";
  const navigate = useNavigate();

  const goHome = () => {
    navigate("/");
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" disableEscapeKeyDown={isHardLock} onClose={isHardLock ? undefined : onClose}>
      <DialogTitle>📢 Admission Notice (Session {academicYear})</DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {`
            Admissions open only for Class Pre-Nursery.
                      
            • Registration is online only via school website  
            • Registration starts: ${publishDate}
            • Only 100 forms (first-come basis)  
            • Form submission ≠ Admission  
            • Admission only after parent + child interview
          `}
        </Typography>

        {isHardLock && (
          <Typography color="error" sx={{ mt: 2, fontWeight: 600 }}>
            🚫 Admission registration has not started yet. Portal will open on 6 February at 10:00 AM.
          </Typography>
        )}
        {isMaxed && (
          <Typography color="error" sx={{ mt: 2, fontWeight: 600 }}>
            ❌ Registration Closed — 100 applications already received.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={goHome}>
          ⬅ Go Back to Home
        </Button>
        {!isHardLock && (
          <Button variant="contained" onClick={onClose}>
            I Understand
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function AdmissionForm() {
  const currentYear = new Date().getFullYear();
  const academicYear = `${currentYear}-${currentYear + 1}`;
  const [formBlocked, setFormBlocked] = useState(false);
  const [publishDate, setPublishDate] = useState('');
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const genderOptions = ['Male', 'Female', 'Other'];
  const [religion, setReligion] = useState('');
  const religionOptions = ['Muslim', 'Hindu', 'Christian', 'Sikh', 'Zain'];
  const [nationality, setNationality] = useState('');
  const [cast, setCast] = useState('');
  const [category, setCategory] = useState('');
  const categoryOptions = ['SC', 'ST', 'OBC', 'GEN', 'Other'];
  const [language, setLanguage] = useState('');
  const languageOptions = ['Hindi', 'English', 'Other'];
  const [fatherName, setFatherName] = useState('');
  const [qualification, setQualifi] = useState('');
  const [fatherOccupation, setFatherOccupation] = useState('');
  const fatherOccupationOptions = ['Service', 'Self-employed', 'Business'];
  const [motherName, setMotherName] = useState('');
  const [mqualification, setMqualifi] = useState('');
  const [motherOccupation, setMotherOccupation] = useState('');
  const motherOccupationOptions = ['Service', 'Self-employed', 'Business', 'House Wife'];
  const [mobNo, setMobNo] = useState('');
  const [whatsappNo, setWhatsappNo] = useState('');
  const [aadharNo, setAadharNo] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [siblings, setSiblings] = useState([]);
  const [reason, setReason] = useState('');
  const [address, setAdd] = useState('');
  const [selectedFatherAadhar, setSelectedFatherAadhar] = useState(null);
  const [fatherAadharSizeExceeds, setFatherAadharSizeExceeds] = useState(null);
  const [selectedMotherAadhar, setSelectedMotherAadhar] = useState(null);
  const [motherAadharSizeExceeds, setMotherAadharSizeExceeds] = useState(null);
  const [selectedBirthCertificate, setSelectedBirthCertificate] = useState(null);
  const [birthCertificateSizeExceeds, setBirthCertificateSizeExceeds] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [guardianSizeExceeds, setGuardianSizeExceeds] = useState(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaxed, setIsMaxed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [termsAcceptedThisSession, setTermsAcceptedThisSession] = useState(false);
  const [modal, setModal] = useState('');
  const [noticeOpen, setNoticeOpen] = useState(true);
  const [lockType, setLockType] = useState("HARD_LOCK");

  useEffect(() => {
    setLockType(getAdmissionWindowStatus(publishDate));
  }, [publishDate]);

  const fetchPublishDate = useCallback(async () => {
    try {
      const response = await fetch(`/api/getPublishDate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const Data = await response.json();
        setPublishDate(Data.PublishDate);
      } else {
        console.error("Error fetching filtered data");
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  }, []);

  const fetchMaxForm = useCallback(async () => {
    try {
      const response = await fetch(`/api/getMaxForms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const Data = await response.json();
        if (Data.recordCount >= 100) {
          setIsMaxed(true);
        } else {
          setIsMaxed(false);
        }
      } else {
        console.error("Error fetching filtered data");
      }
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    }
  }, []);

  useEffect(() => {
    fetchPublishDate();
    fetchMaxForm();
  }, [fetchMaxForm, fetchPublishDate]);

  const handleNameChange = (e) => {
    const value = e.target.value
    setName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      name: value ? '' : 'Please Enter Name'
    }))
  };

  const calculateAge = (dobStr) => {
    if (!dobStr) return null;

    const today = new Date();
    const dobDate = new Date(dobStr);

    let years = today.getFullYear() - dobDate.getFullYear();
    let months = today.getMonth() - dobDate.getMonth();
    let days = today.getDate() - dobDate.getDate();

    if (days < 0) {
      months -= 1;
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return { years, months };
  };

  const isMinAge = (dobStr) => {
    const age = calculateAge(dobStr);
    if (!age) return false;

    if (age.years < 3) return false;

    if (age.years > 4) return false;

    if (age.years === 4 && age.months > 0) return false;

    return true;
  };

  const handleDobChange = (e) => {
    const value = e.target.value;
    setDob(value);

    if (!value) {
      setErrors((prev) => ({
        ...prev, dob: "Please Enter Date Of Birth",
      }));
      setFormBlocked(true);
      return;
    }

    const valid = isMinAge(value);

    if (!valid) {
      setErrors((prev) => ({
        ...prev, dob: "Child age must be between 3 and 4 years.",
      }));
      setFormBlocked(true);
    } else {
      setErrors((prev) => ({
        ...prev, dob: "",
      }));
      setFormBlocked(false);
    }
  };

  const handleGenderChange = (e) => {
    const value = e.target.value
    setGender(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      gender: value ? '' : 'Please Select Gender'
    }))
  };

  const handleReligionChange = (e) => {
    const value = e.target.value
    setReligion(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      religion: value ? '' : 'Please Select Religion'
    }))
  };

  const handleNationChange = (e) => {
    const value = e.target.value
    setNationality(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      nationality: value ? '' : 'Please Enter Nationality'
    }))
  };

  const handleCastChange = (e) => {
    const value = e.target.value
    setCast(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      cast: value ? '' : 'Please Enter Cast'
    }))
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value
    setCategory(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      category: value ? '' : 'Please Select Category'
    }))
  };

  const handleLanguageChange = (e) => {
    const value = e.target.value
    setLanguage(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      language: value ? '' : 'Please Select Language'
    }))
  };

  const handleFatherNameChange = (e) => {
    const value = e.target.value
    setFatherName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      fatherName: value ? '' : 'Please Enter Father Name'
    }))
  };

  const handleQualifiChange = (e) => {
    const value = e.target.value
    setQualifi(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      qualification: value ? '' : 'Please Enter Qualification'
    }))
  };

  const handleFatherOccupationChange = (e) => {
    const value = e.target.value
    setFatherOccupation(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      fatherOccupation: value ? '' : 'Please Select Occupation'
    }))
  };

  const handleMNameChange = (e) => {
    const value = e.target.value
    setMotherName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      motherName: value ? '' : 'Please Enter Mother Name'
    }))
  };

  const handleMqualifiChange = (e) => {
    const value = e.target.value
    setMqualifi(value);
  };

  const handleMotherOccupationChange = (e) => {
    const value = e.target.value
    setMotherOccupation(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      motherOccupation: value ? '' : 'Please Select Occupation'
    }))
  };

  const handleMnoChange = (e) => {
    const value = e.target.value
    setMobNo(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      mobNo: value ? '' : 'Please Enter Mobile No'
    }))
  };

  const handleWnoChange = (e) => {
    const value = e.target.value
    setWhatsappNo(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      whatsappNo: value ? '' : 'Please Enter Whatsapp No'
    }))
  };

  const handleAadharNoChange = (e) => {
    const value = e.target.value
    setAadharNo(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      aadharNo: value ? '' : 'Please Enter Aadhar No'
    }))
  };

  const handleMonthlyIncomeChange = (e) => {
    const value = e.target.value
    setMonthlyIncome(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      monthlyIncome: value ? '' : 'Please Enter Monthly Income'
    }))
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
  };

  const handleAddChange = (e) => {
    setAdd(e.target.value);
  };

  const addSibling = () => {
    setSiblings([...siblings, { sibling_name: '', sibling_age: '', sibling_class: '', sibling_section: '' }]);
  };

  const handleInputChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSiblings = [...siblings];
    updatedSiblings[index][name] = value;
    setSiblings(updatedSiblings);
  };

  const handleFatherAadharUploadClick = () => {
    document.getElementById('FatherAadharUpload').click();
  };

  const handleFatherAadharFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        setFatherAadharSizeExceeds('Father Aadhar uploaded exceeds maximum file size limit (200 KB).');
        setSelectedFatherAadhar(null);
      } else {
        setFatherAadharSizeExceeds('');
        setSelectedFatherAadhar(file);
      }
    }
  };

  const handleFatherAadharRemoveFile = () => {
    setSelectedFatherAadhar(null);
    setFatherAadharSizeExceeds('');
    const fatherAadharUploadInput = document.getElementById('FatherAadharUpload');
    if (fatherAadharUploadInput) {
      fatherAadharUploadInput.value = '';
    }
  };

  const handleMotherAadharUploadClick = () => {
    document.getElementById('MotherAadharUpload').click();
  };

  const handleMotherAadharFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        setMotherAadharSizeExceeds('Mother Aadhar uploaded exceeds maximum file size limit (200 KB).');
        setSelectedMotherAadhar(null);
      } else {
        setMotherAadharSizeExceeds('');
        setSelectedMotherAadhar(file);
      }
    }
  };

  const handleMotherAadharRemoveFile = () => {
    setSelectedMotherAadhar(null);
    setMotherAadharSizeExceeds('');
    const motherAadharUploadInput = document.getElementById('MotherAadharUpload');
    if (motherAadharUploadInput) {
      motherAadharUploadInput.value = '';
    }
  };

  const handleBirthCertificateUploadClick = () => {
    document.getElementById('BirthCertificateUpload').click();
  };

  const handleBirthCertificateFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 200 * 1024) {
        setBirthCertificateSizeExceeds('Birth Certificate uploaded exceeds maximum file size limit (200 KB).');
        setSelectedBirthCertificate(null);
      } else {
        setBirthCertificateSizeExceeds('');
        setSelectedBirthCertificate(file);
      }
    }
  };

  const handleBirthCertificateRemoveFile = () => {
    setSelectedBirthCertificate(null);
    setBirthCertificateSizeExceeds('');
    const birthCertificateUploadInput = document.getElementById('BirthCertificateUpload');
    if (birthCertificateUploadInput) {
      birthCertificateUploadInput.value = '';
    }
  };

  const handleGuardianClick = () => {
    document.getElementById('Guardian').click();
  };

  const handleGuardianChange = (e) => {
    const selectedGuardian = e.target.files[0];
    if (selectedGuardian) {
      setGuardian(selectedGuardian);

      if (selectedGuardian.size > 5000 * 1024) {
        setGuardianSizeExceeds('Family Photo size exceeds 5Mb limit.');
        return;
      } else {
        setGuardianSizeExceeds('');
      }

    } else {
      setGuardian(null);
      setGuardianSizeExceeds('');
    }
  };

  const handleRemoveFile = () => {
    setGuardian(null);
    setGuardianSizeExceeds('');
    const GuardianInput = document.getElementById('Guardian');
    if (GuardianInput) {
      GuardianInput.value = '';
    }
  };

  const handleReset = () => {
    setName('');
    setDob('');
    setGender('');
    setReligion('');
    setNationality('');
    setCategory('');
    setFatherName('');
    setQualifi('');
    setFatherOccupation('');
    setMotherName('');
    setMqualifi('');
    setMotherOccupation('');
    setMobNo('');
    setWhatsappNo('');
    setSiblings([]);
    setReason('');
    setAdd('');
    setSelectedFatherAadhar(null);
    setFatherAadharSizeExceeds('');
    const fatherFileInput = document.getElementById('FatherAadharUpload');
    if (fatherFileInput) {
      fatherFileInput.value = '';
    }
    setSelectedMotherAadhar(null);
    setMotherAadharSizeExceeds('');
    const motherFileInput = document.getElementById('MotherAadharUpload');
    if (motherFileInput) {
      motherFileInput.value = '';
    }
    setSelectedBirthCertificate(null);
    setBirthCertificateSizeExceeds('');
    const bcFileInput = document.getElementById('BirthCertificateUpload');
    if (bcFileInput) {
      bcFileInput.value = '';
    }
    handleRemoveFile();
  }

  const handleSubmit = async (e) => {
    setLoading(true)
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!name || !dob || !gender || !religion || !nationality || !cast || !category || !fatherName || !qualification || !fatherOccupation || !motherName || !mqualification || !motherOccupation || !mobNo || !whatsappNo || !aadharNo || !monthlyIncome || !language || !guardian) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: !name ? 'Name is required' : prevErrors.name,
        dob: !dob ? "Date Of Birth is required" : prevErrors.dob,
        gender: !gender ? "Gender is required" : prevErrors.gender,
        religion: !religion ? "Religion is required" : prevErrors.religion,
        nationality: !nationality ? "Nationality is required" : prevErrors.nationality,
        cast: !cast ? "Cast is required" : prevErrors.cast,
        category: !category ? "Category is required" : prevErrors.category,
        fatherName: !fatherName ? "Fatehr Name is required" : prevErrors.fatherName,
        qualification: !qualification ? "Qualification is required" : prevErrors.qualification,
        fatherOccupation: !fatherOccupation ? "Father Occupation is required" : prevErrors.fatherOccupation,
        motherName: !motherName ? "Mother Name is required" : prevErrors.motherName,
        mqualification: !mqualification ? "Mother Qualification is required" : prevErrors.mqualification,
        motherOccupation: !motherOccupation ? "Mother Occupation is required" : prevErrors.motherOccupation,
        mobNo: !mobNo ? "Mobile No is required" : prevErrors.mobNo,
        whatsappNo: !whatsappNo ? "Whatsapp No is required" : prevErrors.whatsappNo,
        aadharNo: !aadharNo ? "Aadhar No is required" : prevErrors.aadharNo,
        monthlyIncome: !monthlyIncome ? "Monthly Income is required" : prevErrors.monthlyIncome,
        language: !language ? "Language is required" : prevErrors.language,
      }));
      if (!guardian) {
        toast.error("Family Picture Is Required!!")
      }

      setLoading(false);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('dob', dob);
    formData.append('gender', gender);
    formData.append('religion', religion);
    formData.append('nationality', nationality);
    formData.append('cast', cast);
    formData.append('category', category);
    formData.append('language', language);
    formData.append('fatherName', fatherName);
    formData.append('qualification', qualification);
    formData.append('fatherOccupation', fatherOccupation);
    formData.append('motherName', motherName);
    formData.append('mqualification', mqualification);
    formData.append('motherOccupation', motherOccupation);
    formData.append('mobNo', mobNo);
    formData.append('whatsappNo', whatsappNo);
    formData.append('aadharNo', aadharNo);
    formData.append('monthlyIncome', monthlyIncome);
    formData.append('siblings', JSON.stringify(siblings));
    formData.append('reason', reason);
    formData.append('address', address);
    formData.append('selectedFatherAadhar', selectedFatherAadhar);
    formData.append('selectedMotherAadhar', selectedMotherAadhar);
    formData.append('selectedBirthCertificate', selectedBirthCertificate);
    formData.append('guardian', guardian);

    try {
      const response = await fetch(`/api/AdmissionForm`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        toast.success(responseData.message)
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ accepted: true, date: new Date().toISOString() })
        );
        if (responseData.RefNo) {
          setModal(responseData.RefNo)
          downloadReceipt(responseData.RefNo)
        }
        handleReset();
      } else {
        const responseData = await response.json();
        toast.error(responseData.error)
      }
    } catch (error) {
      console.error('Error uploading data:', error);
    } finally {
      setLoading(false)
    }
    setIsSubmitting(false);
  }

  const downloadReceipt = async (refNo) => {
    const response = await fetch("/download-admission-receipt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refNo }),
    });

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `Admission_Receipt.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    if (isMaxed) {
      setNoticeOpen(true);
      setLockType("HARD_LOCK");
    }
  }, [isMaxed]);

  const hasAcceptedTermsPermanently = (() => {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data)?.accepted : false;
  })();

  const canShowForm = lockType === "OPEN" && !isMaxed && (termsAcceptedThisSession || hasAcceptedTermsPermanently);

  return (
    <>
      <InfoNoticeModal open={noticeOpen} lockType={lockType} isMaxed={isMaxed} academicYear={academicYear} publishDate={publishDate} onClose={() => {
        setNoticeOpen(false); if (lockType !== "HARD_LOCK") {
          const data = localStorage.getItem(STORAGE_KEY);
          const accepted = data ? JSON.parse(data)?.accepted : false;

          if (!accepted) {
            setShowTerms(true);
          }
        }
      }} />
      <TermsModal open={showTerms} onAgree={() => { setShowTerms(false); setTermsAcceptedThisSession(true); }} />
      {canShowForm && <div className='my-4'>
        <div className='row mx-2'>
          <div className='max-w-3xl mx-auto text-center mb-8' data-aos="fade-down">
            <h2 className='text-2xl sm:text-2xl font-extrabold tracking-wide text-gray-900 mb-3 center'>ADMISSION FORM FOR ACADEMIC YEAR {academicYear}</h2>
            <h4 className='text-1xl sm:text-2xl font-extrabold tracking-wide text-gray-900 mb-3 center'>CLASS PRE-NURSERY</h4>
            <p className='text-gray-600 text-sm sm:text-base leading-relaxed mb-3' data-aos="fade-up" data-aos-delay="100">If any of the particular given below found incorrect or incomplete,<br />
              the admission form for admission will be rejected</p>
            <p className="font-weight-bold text-center text-uppercase" style={{ fontWeight: '800', textDecoration: 'underline' }} >All fields marked with asterisk (<span className='text-danger'>*</span>)  are mandatory.</p>
          </div>
          <div className='col-12 col-md-6 mb-2' data-aos="fade-up" data-aos-delay="100">
            <TextField label={<span>Full Name of Candidate<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={name} onChange={handleNameChange} error={!!errors.name} helperText={errors.name} disabled={!!formBlocked} />
          </div>
          <div className='col-6 mb-2' data-aos="fade-up" data-aos-delay="200">
            <TextField label={<span>Date of Birth<span className='text-danger'>*</span></span>} variant="outlined" type="date" fullWidth value={dob} onChange={handleDobChange} error={!!errors.dob} helperText={errors.dob} InputLabelProps={{ shrink: true }} />
          </div>
          <div className='col-6 mb-2' data-aos="fade-up" data-aos-delay="300">
            <FormControl variant="outlined" fullWidth error={!!errors.gender} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Gender<span className='text-danger'>*</span></InputLabel>
              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Gender" value={gender} onChange={handleGenderChange}>
                {genderOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.gender}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-6 mb-2' data-aos="fade-up" data-aos-delay="350">
            <FormControl variant="outlined" fullWidth error={!!errors.religion} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Religion<span className='text-danger'>*</span></InputLabel>
              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Religion" value={religion} onChange={handleReligionChange}>
                {religionOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.religion}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="400">
            <TextField label={<span>Nationality<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={nationality} onChange={handleNationChange} error={!!errors.nationality} helperText={errors.nationality} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="400">
            <TextField label={<span>Cast<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={cast} onChange={handleCastChange} error={!!errors.cast} helperText={errors.cast} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="450">
            <FormControl variant="outlined" fullWidth error={!!errors.category} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Category<span className='text-danger'>*</span></InputLabel>
              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Ctegory" value={category} onChange={handleCategoryChange}>
                {categoryOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.category}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-md-3 mb-2' data-aos="fade-up" data-aos-delay="900">
            <FormControl fullWidth error={!!errors.language} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Mother Tongue<span className='text-danger'>*</span></InputLabel>
              <Select labelid="demo-simple-select-label" id="demo-simple-select" label="Mother Tongue" value={language} onChange={handleLanguageChange}>
                {languageOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.language}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-md-6 mb-2' data-aos="fade-up" data-aos-delay="500">
            <TextField label={<span>Father's Name<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={fatherName} onChange={handleFatherNameChange} error={!!errors.fatherName} helperText={errors.fatherName} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="550">
            <TextField label={<span>Qualification<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={qualification} onChange={handleQualifiChange} error={!!errors.qualification} helperText={errors.qualification} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="600">
            <FormControl variant="outlined" fullWidth error={!!errors.fatherOccupation} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Occupation<span className='text-danger'>*</span></InputLabel>
              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Occupation" value={fatherOccupation} onChange={handleFatherOccupationChange}>
                {fatherOccupationOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.fatherOccupation}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-md-6 mb-2' data-aos="fade-up" data-aos-delay="650">
            <TextField label={<span>Mother's Name<span className='text-danger'>*</span></span>} variant="outlined" fullWidth value={motherName} onChange={handleMNameChange} error={!!errors.motherName} helperText={errors.motherName} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="700">
            <TextField label={<span>Qualification</span>} variant="outlined" fullWidth value={mqualification} onChange={handleMqualifiChange} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="750">
            <FormControl fullWidth error={!!errors.motherOccupation} disabled={!!formBlocked}>
              <InputLabel id="demo-simple-select-label">Occupation<span className='text-danger'>*</span></InputLabel>
              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Occupation" value={motherOccupation} onChange={handleMotherOccupationChange}>
                {motherOccupationOptions.map((option, index) => (
                  <MenuItem key={index} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{errors.motherOccupation}</FormHelperText>
            </FormControl>
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="800">
            <TextField label={<span>Mobile No<span className='text-danger'>*</span></span>} variant="outlined" type="number" fullWidth value={mobNo} onChange={handleMnoChange} error={!!errors.mobNo} helperText={errors.mobNo} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="850">
            <TextField label={<span>Whatsapp No<span className='text-danger'>*</span></span>} variant="outlined" type="number" fullWidth value={whatsappNo} onChange={handleWnoChange} error={!!errors.whatsappNo} helperText={errors.whatsappNo} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="850">
            <TextField label={<span>Aadhar No<span className='text-danger'>*</span></span>} variant="outlined" type="number" fullWidth value={aadharNo} onChange={handleAadharNoChange} error={!!errors.aadharNo} helperText={errors.aadharNo} disabled={!!formBlocked} />
          </div>
          <div className='col-6 col-md-3 mb-2' data-aos="fade-up" data-aos-delay="850">
            <TextField label={<span>Monthly Income<span className='text-danger'>*</span></span>} variant="outlined" type="number" fullWidth value={monthlyIncome} onChange={handleMonthlyIncomeChange} error={!!errors.monthlyIncome} helperText={errors.monthlyIncome} disabled={!!formBlocked} />
          </div>
          <div className="d-flex flex-column" data-aos="fade-up" data-aos-delay="950">
            <label htmlFor="address">Give The details of Siblings/ Real brother or sister <span className='text-danger'>(Not Cousins)</span> If studying in <span className="scholarVarsity">Fatehpur Public School</span>:</label>
            {siblings.map((sibling, index) => (
              <div className="row" key={index}>
                <div className="col">
                  <label htmlFor="sibling_name">NAME</label>
                  <input type="text" className="form-control" id="sibling_name" name="sibling_name" onChange={(e) => handleInputChange(index, e)} />
                </div>
                <div className="col">
                  <label htmlFor="sibling_age">AGE</label>
                  <input type="text" className="form-control" id="sibling_age" name="sibling_age" onChange={(e) => handleInputChange(index, e)} />
                </div>
                <div className="col">
                  <label htmlFor="sibling_class">CLASS</label>
                  <input type="text" className="form-control" id="sibling_class" name="sibling_class" onChange={(e) => handleInputChange(index, e)} />
                </div>
                <div className="col">
                  <label htmlFor="sibling_section">SECTION</label>
                  <input type="text" className="form-control" id="sibling_section" name="sibling_section" onChange={(e) => handleInputChange(index, e)} />
                </div>
              </div>
            ))}
            <Button variant='contained' color='primary' className='my-2' onClick={addSibling} disabled={!!formBlocked}>Add Sibling</Button>
          </div>
          <div className="col-12 my-2" data-aos="fade-up" data-aos-delay="100">
            <TextField id="filled-multiline-flexible" label="Reason For Seeking Admission In This School" multiline maxRows={10} variant="outlined" fullWidth value={reason} onChange={handleReasonChange} disabled={!!formBlocked} />
          </div>
          <div className="col-12 my-2" data-aos="fade-up" data-aos-delay="1050">
            <TextField id="filled-multiline-flexible" label="Address" multiline maxRows={10} variant="outlined" fullWidth value={address} onChange={handleAddChange} disabled={!!formBlocked} />
          </div>
          <div className='col-md-4 mb-2'>
            <label className='form-label' htmlFor="title">Father Aadhar Card:</label>
            <div className={`d-flex items-center border rounded-4 ${selectedFatherAadhar ? 'd-none' : ''}`} onClick={handleFatherAadharUploadClick} >
              <div>
                <button className="me-2 rounded-4 btn btn-info" type="button" >Upload</button>
              </div>
              <div className="narrowFormats"><p className='mb-0'>DOC, DOCx, PDF, RTF | Max: 200 KB</p></div>
            </div>
            <input className="uploadAction d-none" type="file" id="FatherAadharUpload" accept=".doc,.docx,.pdf,.rtf" onChange={handleFatherAadharFileChange} />
            {selectedFatherAadhar && (
              <div className='mt-1'>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>{selectedFatherAadhar.name}</strong> ({(selectedFatherAadhar.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                  <button className="btn btn-outline-danger btn-sm ms-2" type="button" onClick={handleFatherAadharRemoveFile}>Remove</button>
                </div>
              </div>
            )}
            {fatherAadharSizeExceeds && (<span className='text-danger' style={{ fontSize: '12px' }}>{fatherAadharSizeExceeds}</span>)}
          </div>
          <div className='col-md-4 mb-2'>
            <label className='form-label' htmlFor="title">Mother Aadhar Card:</label>
            <div className={`d-flex items-center border rounded-4 ${selectedMotherAadhar ? 'd-none' : ''}`} onClick={handleMotherAadharUploadClick} >
              <div>
                <button className="me-2 rounded-4 btn btn-secondary" type="button" >Upload</button>
              </div>
              <div className="narrowFormats"><p className='mb-0'>DOC, DOCx, PDF, RTF | Max: 200 KB</p></div>
            </div>
            <input className="uploadAction d-none" type="file" id="MotherAadharUpload" accept=".doc,.docx,.pdf,.rtf" onChange={handleMotherAadharFileChange} />
            {selectedMotherAadhar && (
              <div className='mt-1'>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>{selectedMotherAadhar.name}</strong> ({(selectedMotherAadhar.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                  <button className="btn btn-outline-danger btn-sm ms-2" type="button" onClick={handleMotherAadharRemoveFile}>Remove</button>
                </div>
              </div>
            )}
            {motherAadharSizeExceeds && (<span className='text-danger' style={{ fontSize: '12px' }}>{motherAadharSizeExceeds}</span>)}
          </div>
          <div className='col-md-4 mb-2'>
            <label className='form-label' htmlFor="title">Birth Certificate:</label>
            <div className={`d-flex items-center border rounded-4 ${selectedBirthCertificate ? 'd-none' : ''}`} onClick={handleBirthCertificateUploadClick} >
              <div>
                <button className="me-2 rounded-4 btn btn-danger" type="button" >Upload</button>
              </div>
              <div className="narrowFormats"><p className='mb-0'>DOC, DOCx, PDF, RTF | Max: 200 KB</p></div>
            </div>
            <input className="uploadAction d-none" type="file" id="BirthCertificateUpload" accept=".doc,.docx,.pdf,.rtf" onChange={handleBirthCertificateFileChange} />
            {selectedBirthCertificate && (
              <div className='mt-1'>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>{selectedBirthCertificate.name}</strong> ({(selectedBirthCertificate.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                  <button className="btn btn-outline-danger btn-sm ms-2" type="button" onClick={handleBirthCertificateRemoveFile}>Remove</button>
                </div>
              </div>
            )}
            {birthCertificateSizeExceeds && (<span className='text-danger' style={{ fontSize: '12px' }}>{birthCertificateSizeExceeds}</span>)}
          </div>
          <div className='col-12 mb-2'>
            <div className={`d-flex items-center border rounded-4 mt-2 ${guardian ? 'd-none' : ''}`} onClick={handleGuardianClick}>
              <div>
                <button className="me-2 rounded-4 btn btn-danger" type="button" >Upload Family Picture</button>
              </div>
              <div className="narrowFormats"><p className='mb-0'>JPEG, JPG, PNG | Max: 5 MB</p></div>
            </div>
            <input className="d-none" type="file" id="Guardian" accept="image/*" an onChange={handleGuardianChange} />
            {guardian && (
              <div className='mt-1'>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>{guardian.name}</strong> ({(guardian.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                  <button className="btn btn-outline-danger btn-sm ms-2" type="button" onClick={handleRemoveFile}>Remove</button>
                </div>
              </div>
            )}
            {guardianSizeExceeds && (<span className='text-danger' style={{ fontSize: '12px' }}>{guardianSizeExceeds}</span>)}
          </div>
          <div className="col-12 mt-3">
            <FormControlLabel
              control={
                <Checkbox
                  checked={declarationAccepted}
                  onChange={(e) => setDeclarationAccepted(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I hereby declare that all the information provided in this admission form is true and correct to the best of my knowledge.
                  I understand that if any information is found false, the school may reject/cancel the admission.
                </Typography>
              }
            />
          </div>
        </div>
        <div className='d-flex justify-content-center' data-aos="fade-up" data-aos-delay="1100">
          <Button variant='contained' color='error' onClick={handleSubmit} disabled={formBlocked || !declarationAccepted}>Submit</Button>
        </div>
        <Dialog open={modal} onClose={() => setModal('')} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 600 }}>
            🎉 Admission Form Submitted Successfully
          </DialogTitle>

          <DialogContent>

            <div className="bg-green-50 border border-green-600 text-green-800 p-4 rounded-md text-center mb-4">
              <p className="text-sm">Your Application Reference Number</p>
              <h2 className="text-2xl font-bold tracking-widest mt-1">
                {modal}
              </h2>
            </div>

            <p className="text-sm mb-2">
              Please save this <strong>Reference Number</strong> carefully. It will be required to check your child's admission status in the future.
            </p>

            <p className="text-sm mb-2">
              Along with this Reference Number, you will also need to enter the <strong>Student's Date of Birth</strong> to track the application.
            </p>

            <p className="text-sm text-gray-600">
              We recommend taking a screenshot or writing it down for safe keeping.
            </p>

          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button variant="contained" color="primary" onClick={() => setModal('')}>
              OK, I have saved it
            </Button>
          </DialogActions>
        </Dialog>
      </div>}
      {loading && (
        <div className="modal-backdrop show"></div>
      )}
      <div className={`modal fade ${loading ? 'show' : ''}`} style={{ display: loading ? 'block' : 'none' }} tabIndex="-1" role="dialog">
        <div id="preloader">
          <div className="bar-container">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
          <h1>Loading...</h1>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}
